import React, { useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  MenuItem,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import { Add, DeleteOutline, Edit } from '@mui/icons-material';
import {
  useTaskTypes,
  useTaskPriorities,
} from '../hooks/reactQuerys/TaskAutomationQueries';
import {
  useGetUsers,
  useGetUserGroups,
} from '../hooks/reactQuerys/UserQueries';
import { ResponsibleAutocomplete } from '../generic/ResponsibleAutocomplete';
import { ITaskAutomation } from '../Interfaces/ITaskAutomation';
import { ILegalRequirementState } from '../Interfaces/ILegalRequirementState';

interface IProps {
  /** Aufgaben-Definitionen (Child-Array am Template ODER an der Vorgangs-Maßnahme). */
  items: ITaskAutomation[];
  onChange: (items: ITaskAutomation[]) => void;
  legalRequirementStates: ILegalRequirementState[];
  /** Änderungen gesperrt (z. B. Maßnahme mit Status „Erledigt" gespeichert). */
  locked?: boolean;
}

const emptyAutomation = (items: ITaskAutomation[]): ITaskAutomation => {
  const nextId =
    items.reduce((min, a) => Math.min(min, a.idTaskAutomation), 0) - 1;
  return {
    idTaskAutomation: nextId,
    // Zuordnung (Template vs. Vorgangs-Maßnahme) setzt das Backend beim Speichern.
    idLegalRequirementTemplate: null,
    idLegalRequirement: null,
    idLegalRequirementState: -1,
    Title: '',
    Body: '',
    ProcessingTime: 14,
    idTaskType: 1,
    idTaskPriority: 3,
    User_idUser: null,
    UserGroup_idUserGroup: null,
    AssignToTransactionResponsible: true,
    activeTaskAutomation: true,
  };
};

/**
 * #1244 — Pflege der Aufgaben-Automationen an einer Rechtsgeschäftsart-Maßnahme:
 * je Trigger-Status eine Aufgaben-Vorlage. Rein kontrolliert auf einem Array —
 * die Persistenz erfolgt mit dem Rechtsgeschäftsart-fullObject (kein eigener
 * Request). Beim Erreichen des Status im Vorgang wird die Aufgabe automatisch
 * (über den ProcessManager) erzeugt.
 */
export const TaskAutomationManager: React.FC<IProps> = ({
  items,
  onChange,
  legalRequirementStates,
  locked = false,
}) => {
  const { taskTypes } = useTaskTypes();
  const { taskPriorities } = useTaskPriorities();
  const { users } = useGetUsers();
  const { userGroups } = useGetUserGroups();

  const [editItem, setEditItem] = useState<ITaskAutomation | null>(null);

  const stateName = (id: number) =>
    legalRequirementStates.find(s => s.idLegalRequirementState === id)
      ?.LegalRequirementState ?? `Status ${id}`;
  const userName = (id?: number | null) => {
    const u = (users ?? []).find(x => x.idUser === id);
    return u ? `${u.FirstName} ${u.LastName}` : '';
  };
  const groupName = (id?: number | null) =>
    (userGroups ?? []).find(g => g.idUserGroup === id)?.UserGroup ?? '';

  const responsibilityLabel = (a: ITaskAutomation) => {
    if (a.AssignToTransactionResponsible) return 'Zuständige(r) des Vorgangs';
    if (a.User_idUser) return userName(a.User_idUser);
    if (a.UserGroup_idUserGroup)
      return `Gruppe: ${groupName(a.UserGroup_idUserGroup)}`;
    return '—';
  };

  const handleSave = () => {
    if (!editItem) return;
    if (editItem.idLegalRequirementState <= 0 || !editItem.Title.trim()) return;
    const exists = items.some(
      a => a.idTaskAutomation === editItem.idTaskAutomation
    );
    const next = exists
      ? items.map(a =>
          a.idTaskAutomation === editItem.idTaskAutomation ? editItem : a
        )
      : [...items, editItem];
    onChange(next);
    setEditItem(null);
  };

  const handleDelete = (a: ITaskAutomation) => {
    onChange(items.filter(x => x.idTaskAutomation !== a.idTaskAutomation));
  };

  return (
    <Box>
      <Stack
        direction='row'
        justifyContent='space-between'
        alignItems='center'
        sx={{ mb: 1 }}
      >
        <Typography variant='body2' color='text.secondary'>
          Beim Erreichen des Trigger-Status wird die Aufgabe automatisch erzeugt
          (Deadline = Statusdatum + Bearbeitungszeit). Änderungen werden beim
          Speichern übernommen.
        </Typography>
        <Button
          size='small'
          variant='outlined'
          startIcon={<Add />}
          disabled={locked}
          onClick={() => setEditItem(emptyAutomation(items))}
        >
          Neue Aufgabe
        </Button>
      </Stack>

      {locked && (
        <Alert severity='info' sx={{ mb: 1 }}>
          Die Maßnahme ist mit Status „Erledigt" gespeichert — Aufgaben sind
          gesperrt.
        </Alert>
      )}

      {items.length === 0 ? (
        <Alert severity='info' variant='outlined'>
          Noch keine Aufgaben-Automationen hinterlegt.
        </Alert>
      ) : (
        <Table size='small'>
          <TableHead>
            <TableRow>
              <TableCell>Trigger-Status</TableCell>
              <TableCell>Titel</TableCell>
              <TableCell>Bearb.-Zeit</TableCell>
              <TableCell>Zuständigkeit</TableCell>
              <TableCell align='right'>Aktion</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {items.map(a => (
              <TableRow key={a.idTaskAutomation}>
                <TableCell>
                  <Chip
                    size='small'
                    label={stateName(a.idLegalRequirementState)}
                  />
                </TableCell>
                <TableCell>{a.Title}</TableCell>
                <TableCell>{a.ProcessingTime} Tg.</TableCell>
                <TableCell>{responsibilityLabel(a)}</TableCell>
                <TableCell align='right'>
                  <Tooltip title='Bearbeiten'>
                    <span>
                      <IconButton
                        size='small'
                        disabled={locked}
                        onClick={() => setEditItem({ ...a })}
                      >
                        <Edit fontSize='small' />
                      </IconButton>
                    </span>
                  </Tooltip>
                  <Tooltip title='Entfernen'>
                    <span>
                      <IconButton
                        size='small'
                        disabled={locked}
                        onClick={() => handleDelete(a)}
                      >
                        <DeleteOutline fontSize='small' />
                      </IconButton>
                    </span>
                  </Tooltip>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      )}

      <Dialog
        open={editItem !== null}
        onClose={() => setEditItem(null)}
        maxWidth='sm'
        fullWidth
      >
        <DialogTitle>
          {editItem && editItem.idTaskAutomation < 0
            ? 'Neue Aufgaben-Automation'
            : 'Aufgaben-Automation bearbeiten'}
        </DialogTitle>
        <DialogContent>
          {editItem && (
            <Stack spacing={2} sx={{ mt: 1 }}>
              <TextField
                select
                label='Trigger-Status'
                value={
                  editItem.idLegalRequirementState > 0
                    ? String(editItem.idLegalRequirementState)
                    : ''
                }
                onChange={e =>
                  setEditItem({
                    ...editItem,
                    idLegalRequirementState: Number(e.target.value),
                  })
                }
              >
                {legalRequirementStates.map(s => (
                  <MenuItem
                    key={s.idLegalRequirementState}
                    value={s.idLegalRequirementState}
                  >
                    {s.LegalRequirementState}
                  </MenuItem>
                ))}
              </TextField>
              <TextField
                label='Titel der Aufgabe'
                value={editItem.Title}
                onChange={e =>
                  setEditItem({ ...editItem, Title: e.target.value })
                }
                error={!editItem.Title.trim()}
              />
              <TextField
                label='Beschreibung'
                value={editItem.Body ?? ''}
                onChange={e =>
                  setEditItem({ ...editItem, Body: e.target.value })
                }
                multiline
                rows={3}
              />
              <Stack direction='row' spacing={2}>
                <TextField
                  label='Bearbeitungszeit (Tage)'
                  type='number'
                  value={editItem.ProcessingTime}
                  onChange={e =>
                    setEditItem({
                      ...editItem,
                      ProcessingTime: Number(e.target.value),
                    })
                  }
                  sx={{ width: 200 }}
                />
                <TextField
                  select
                  label='Aufgabentyp'
                  value={String(editItem.idTaskType ?? 1)}
                  onChange={e =>
                    setEditItem({
                      ...editItem,
                      idTaskType: Number(e.target.value),
                    })
                  }
                  sx={{ flex: 1 }}
                >
                  {taskTypes.map(t => (
                    <MenuItem key={t.idTaskType} value={t.idTaskType}>
                      {t.TaskType}
                    </MenuItem>
                  ))}
                </TextField>
                <TextField
                  select
                  label='Priorität'
                  value={String(editItem.idTaskPriority ?? 3)}
                  onChange={e =>
                    setEditItem({
                      ...editItem,
                      idTaskPriority: Number(e.target.value),
                    })
                  }
                  sx={{ flex: 1 }}
                >
                  {taskPriorities.map(p => (
                    <MenuItem key={p.idTaskPriority} value={p.idTaskPriority}>
                      {p.TaskPriority}
                    </MenuItem>
                  ))}
                </TextField>
              </Stack>
              <TextField
                select
                label='Zuständigkeit'
                value={
                  editItem.AssignToTransactionResponsible
                    ? 'transaction'
                    : 'specific'
                }
                onChange={e =>
                  setEditItem({
                    ...editItem,
                    AssignToTransactionResponsible:
                      e.target.value === 'transaction',
                    ...(e.target.value === 'transaction'
                      ? { User_idUser: null, UserGroup_idUserGroup: null }
                      : {}),
                  })
                }
                helperText='Standard: der dem Vorgang zugeordnete Mitarbeiter bzw. die zugeordnete Gruppe erhält die Aufgabe.'
              >
                <MenuItem value='transaction'>
                  Zuständige(r) des Vorgangs (Standard)
                </MenuItem>
                <MenuItem value='specific'>
                  Bestimmte(r) Mitarbeiter / Gruppe
                </MenuItem>
              </TextField>
              {!editItem.AssignToTransactionResponsible && (
                <ResponsibleAutocomplete
                  users={users ?? []}
                  userGroups={userGroups ?? []}
                  idUser={editItem.User_idUser}
                  idUserGroup={editItem.UserGroup_idUserGroup}
                  onChange={sel =>
                    setEditItem({
                      ...editItem,
                      User_idUser: sel.idUser,
                      UserGroup_idUserGroup: sel.idUserGroup,
                    })
                  }
                />
              )}
            </Stack>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setEditItem(null)}>Abbruch</Button>
          <Button variant='contained' onClick={() => handleSave()}>
            Übernehmen
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};
